// The Studio copilot chat panel — docked as an editor-side tab in the guide
// and skill editors. Context-aware: every turn ships the current draft, so
// the assistant always reasons about unsaved edits. The copilot never writes;
// its proposals surface as PatchCards the user applies into the form.

import { useEffect, useRef, useState } from "react";
import type {
  AssistantContext,
  AssistantMessage,
  AssistantModel,
  PatchProposal,
  ToolTraceEntry,
} from "../../services/assistant";
import { listAssistantModels, sendAssistantTurn } from "../../services/assistant";
import { PatchCard } from "./PatchCard";

const MODEL_STORAGE_KEY = "studio-copilot-model";

interface ChatEntry {
  message: AssistantMessage;
  // Attached to assistant entries only:
  proposals?: PatchProposal[];
  toolTrace?: ToolTraceEntry[];
}

interface Props {
  context: AssistantContext;
  // Current editable form state, for diff rendering (guide profile or skill).
  current: Record<string, unknown>;
  onApplyPatch: (patch: Record<string, unknown>) => void;
}

export function CopilotPanel({ context, current, onApplyPatch }: Props) {
  const [models, setModels] = useState<AssistantModel[]>([]);
  const [model, setModel] = useState<string>(
    () => localStorage.getItem(MODEL_STORAGE_KEY) || "",
  );
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listAssistantModels()
      .then((r) => {
        setModels(r.models);
        setModel((m) => (m && r.models.some((x) => x.id === m) ? m : r.default));
      })
      .catch((e) => setError(`Copilot unavailable: ${e.message}`));
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [entries, busy]);

  function pickModel(id: string) {
    setModel(id);
    localStorage.setItem(MODEL_STORAGE_KEY, id);
  }

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    setError("");
    setInput("");
    const history: AssistantMessage[] = [
      ...entries.map((e) => e.message),
      { role: "user", content: text },
    ];
    setEntries((prev) => [...prev, { message: { role: "user", content: text } }]);
    setBusy(true);
    try {
      const reply = await sendAssistantTurn(model || null, context, history);
      setEntries((prev) => [
        ...prev,
        {
          message: { role: "assistant", content: reply.message },
          proposals: reply.proposals,
          toolTrace: reply.toolTrace,
        },
      ]);
    } catch (e) {
      setError(`Turn failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="copilot">
      <div className="copilot-header">
        <select
          value={model}
          onChange={(e) => pickModel(e.target.value)}
          title="Copilot model"
        >
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.id}
            </option>
          ))}
          {!models.length && model && <option value={model}>{model}</option>}
        </select>
        <div className="spacer" />
        <button
          className="btn btn-sm"
          onClick={() => setEntries([])}
          disabled={!entries.length || busy}
        >
          New chat
        </button>
      </div>

      <div className="copilot-messages" ref={scrollRef}>
        {!entries.length && (
          <div className="muted copilot-hint">
            Ask for help writing or tightening the {context.kind}'s prompt — e.g. "why is
            this guide scoring low on conciseness?" or "make the persona warmer". Suggested
            edits appear as diffs you can apply.
          </div>
        )}
        {entries.map((entry, i) => (
          <div key={i} className={`copilot-msg copilot-${entry.message.role}`}>
            {entry.toolTrace && entry.toolTrace.length > 0 && (
              <div className="copilot-trace">
                {entry.toolTrace.map((t, j) => (
                  <span key={j} className="copilot-trace-chip" title={t.tool}>
                    {t.summary}
                  </span>
                ))}
              </div>
            )}
            {entry.message.content && (
              <div className="copilot-bubble">{entry.message.content}</div>
            )}
            {entry.proposals?.map((p, j) => (
              <PatchCard key={j} proposal={p} current={current} onApply={onApplyPatch} />
            ))}
          </div>
        ))}
        {busy && <div className="muted copilot-hint">thinking…</div>}
      </div>

      {error && <div className="callout">{error}</div>}

      <div className="copilot-composer">
        <textarea
          rows={2}
          value={input}
          placeholder="Ask the copilot…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <button className="btn btn-primary" onClick={send} disabled={busy || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
}
